// Domain analysis engine for comprehensive heuristic scoring and insights generation
// Factor weights: Length (15%), Word/Pronounce (12%), Clarity (12%), Brandability (14%),
// Keyword/Intent (12%), TLD Trust/Fit (12%), Memorability (10%), Versatility (6%),
// Liquidity (5%), Bundle Potential (2%)
// Score remapped to 50-100 range for positive messaging
// Positive-language policy: all outputs emphasize strengths and opportunities

export interface DomainAnalysis {
  length: number;
  words: number;
  hasHyphen: boolean;
  hasNumber: boolean;
  tld: string;
  tldTier: 'premium' | 'mainstream' | 'niche';
  tldLabel: string; // Positive descriptor
  brandability: 'high' | 'med' | 'low';
  clarity: 'high' | 'med' | 'low';
  keywordStrength: 'high' | 'med' | 'low';
  memorability: 'high' | 'med' | 'low';
  versatility: 'high' | 'med' | 'low';
  liquidity: 'high' | 'med' | 'low';
  nicheHints: string[];
  quickReasons: string[];
  score: number; // 50-100 friendly remapped score
}

// Expanded keyword list for comprehensive commercial intent detection
const STRONG_KEYWORDS = [
  // Tech & AI
  'ai',
  'data',
  'cloud',
  'tech',
  'digital',
  'online',
  'web',
  'app',
  'mobile',
  'dev',
  'code',
  'software',
  'saas',
  'platform',
  'api',
  'crypto',
  'blockchain',
  'fintech',

  // Business & Services
  'studio',
  'agency',
  'consulting',
  'solutions',
  'services',
  'systems',
  'ventures',
  'labs',
  'hub',
  'base',
  'works',
  'pro',
  'expert',
  'premium',
  'elite',
  'prime',

  // Commercial Intent
  'buy',
  'sell',
  'rent',
  'book',
  'shop',
  'store',
  'market',
  'pay',
  'hire',
  'jobs',
  'deal',
  'price',
  'cost',
  'cheap',
  'best',
  'top',
  'fast',
  'quick',
  'easy',

  // Industries
  'marketing',
  'media',
  'creative',
  'design',
  'brand',
  'legal',
  'law',
  'finance',
  'health',
  'medical',
  'edu',
  'learn',
  'training',
  'real',
  'estate',
  'property',
  'home',
  'house',
  'food',
  'restaurant',
  'travel',
  'auto',
  'insurance',
  'insure',
  'eco',
  'green',
  'clean',
  'build',
  'construction',

  // Generic Business Terms
  'news',
  'blog',
  'social',
  'network',
  'connect',
  'community',
  'group',
  'team',
];

// TLD tier classification with positive labels
const TLD_CONFIG = {
  premium: {
    extensions: ['.com', '.io', '.ai', '.co'],
    label: 'High-demand TLD',
  },
  mainstream: {
    extensions: ['.net', '.org', '.app', '.dev', '.gg', '.so', '.xyz'],
    label: 'Widely recognized TLD',
  },
  niche: {
    extensions: ['.info', '.biz', '.us', '.uk', '.ca', '.de', '.fr', '.me', '.tv'],
    label: 'Distinctive niche TLD',
  },
};

function getTldTier(tld: string): 'premium' | 'mainstream' | 'niche' {
  const lowerTld = tld.toLowerCase();

  for (const [tier, config] of Object.entries(TLD_CONFIG)) {
    if (config.extensions.includes(lowerTld)) {
      return tier as 'premium' | 'mainstream' | 'niche';
    }
  }

  return 'niche';
}

function getTldLabel(tldTier: 'premium' | 'mainstream' | 'niche'): string {
  return TLD_CONFIG[tldTier].label;
}

function extractWords(domain: string): string[] {
  const name = domain.split('.')[0];
  return name.split(/[-.]|(?=[A-Z])/).filter((word) => word.length > 0);
}

// Calculate length & brevity score (0-100)
function calculateLengthScore(length: number): number {
  if (length <= 5) return 100;
  if (length <= 8) return 90;
  if (length <= 12) return 80;
  if (length <= 15) return 70;
  if (length <= 20) return 60;
  return 55;
}

// Calculate word count & pronounceability score (0-100)
function calculatePronounceabilityScore(words: string[], hasHyphen: boolean): number {
  let score = 0;

  // Word count factor
  if (words.length <= 2) score += 50;
  else if (words.length <= 3) score += 35;
  else score += 20;

  // Vowel/consonant alternation check
  const hasGoodFlow = words.some((word) => {
    const vowelPattern = /[aeiou]/gi;
    const matches = word.match(vowelPattern);
    return matches && matches.length >= word.length * 0.3;
  });

  if (hasGoodFlow) score += 30;
  else score += 15;

  // Hyphen penalty (but keep positive)
  if (!hasHyphen) score += 20;
  else score += 10;

  return Math.min(100, score);
}

// Calculate clarity & spellability score (0-100)
function calculateClarityScore(length: number, hasHyphen: boolean, hasNumber: boolean): number {
  let score = 60; // Base positive score

  if (!hasHyphen) score += 20;
  if (!hasNumber) score += 20;

  return Math.min(100, score);
}

// Calculate brandability score (0-100)
function calculateBrandabilityScore(length: number, words: string[]): number {
  let score = 0;

  // Length factor
  if (length <= 8) score += 40;
  else if (length <= 12) score += 30;
  else score += 15;

  // Word structure
  if (words.length <= 2) score += 30;
  else score += 15;

  // Brand suffix detection
  const brandSuffixes = ['ly', 'labs', 'hub', 'base', 'works', 'app', 'pro'];
  const hasBrandSuffix = brandSuffixes.some((suffix) =>
    words.some((word) => word.toLowerCase().endsWith(suffix)),
  );

  if (hasBrandSuffix) score += 30;
  else score += 15;

  return Math.min(100, score);
}

// Calculate keyword & commercial intent score (0-100)
function calculateKeywordScore(words: string[]): number {
  let score = 0;
  const lowerWords = words.map((w) => w.toLowerCase());

  // Strong keyword match
  const hasStrongKeyword = lowerWords.some(
    (word) =>
      STRONG_KEYWORDS.includes(word) || STRONG_KEYWORDS.some((keyword) => word.includes(keyword)),
  );

  if (hasStrongKeyword) score += 60;
  else score += 20;

  // Commercial intent terms
  const commercialTerms = ['buy', 'sell', 'rent', 'book', 'shop', 'store', 'pay', 'hire'];
  const hasCommercial = lowerWords.some((word) =>
    commercialTerms.some((term) => word.includes(term)),
  );

  if (hasCommercial) score += 40;
  else score += 20;

  return Math.min(100, score);
}

// Calculate TLD trust & fit score (0-100)
function calculateTldScore(tldTier: 'premium' | 'mainstream' | 'niche'): number {
  switch (tldTier) {
    case 'premium':
      return 100;
    case 'mainstream':
      return 75;
    case 'niche':
      return 60; // Still positive
  }
}

// Calculate memorability score (0-100)
function calculateMemorabilityScore(length: number, words: string[]): number {
  let score = 0;

  // Length factor
  if (length <= 8) score += 50;
  else if (length <= 12) score += 35;
  else score += 20;

  // Syllable feel (approximation)
  const totalChars = words.join('').length;
  const avgWordLength = totalChars / words.length;

  if (avgWordLength <= 4) score += 30;
  else if (avgWordLength <= 6) score += 20;
  else score += 10;

  // Word count
  if (words.length <= 2) score += 20;
  else score += 10;

  return Math.min(100, score);
}

// Calculate versatility score (0-100)
function calculateVersatilityScore(words: string[]): number {
  const genericTerms = [
    'studio',
    'labs',
    'media',
    'ventures',
    'solutions',
    'systems',
    'hub',
    'base',
  ];
  const lowerWords = words.map((w) => w.toLowerCase());

  const hasGeneric = lowerWords.some((word) => genericTerms.some((term) => word.includes(term)));

  return hasGeneric ? 85 : 65; // Always positive
}

// Calculate liquidity score (0-100)
function calculateLiquidityScore(
  tldTier: 'premium' | 'mainstream' | 'niche',
  length: number,
  hasBin: boolean,
): number {
  let score = 50; // Base

  // TLD factor
  if (tldTier === 'premium') score += 30;
  else if (tldTier === 'mainstream') score += 20;
  else score += 10;

  // Length factor
  if (length <= 8) score += 15;
  else score += 5;

  // BIN presence
  if (hasBin) score += 5;

  return Math.min(100, score);
}

// Calculate bundle potential score (0-100)
function calculateBundlePotentialScore(words: string[]): number {
  const coreWords = ['app', 'tech', 'data', 'media', 'studio', 'labs'];
  const lowerWords = words.map((w) => w.toLowerCase());

  const hasCoreWord = lowerWords.some((word) => coreWords.some((core) => word.includes(core)));

  return hasCoreWord ? 80 : 60; // Always positive
}

function generateNicheHints(words: string[], tld: string, tags: string[] = []): string[] {
  const hints: string[] = [];

  // From TLD
  const tldHints: { [key: string]: string } = {
    '.ai': 'AI & Machine Learning',
    '.io': 'Technology & SaaS',
    '.app': 'Mobile & Applications',
    '.dev': 'Development & Engineering',
    '.co': 'Startups & Business',
    '.tech': 'Technology',
  };

  if (tldHints[tld]) hints.push(tldHints[tld]);

  // From keywords
  const lowerWords = words.map((w) => w.toLowerCase());
  const keywordMap: { [key: string]: string } = {
    'tech|ai|data|cloud|digital': 'Technology',
    'design|creative|studio|media': 'Creative & Design',
    'legal|law|consulting': 'Professional Services',
    'shop|store|market|ecommerce': 'E-commerce & Retail',
    'health|medical|care': 'Healthcare & Wellness',
    'finance|fintech|pay': 'Finance & Fintech',
    'edu|learn|training': 'Education & Training',
    'marketing|advertising': 'Marketing & Advertising',
  };

  for (const [keywords, category] of Object.entries(keywordMap)) {
    const keywordList = keywords.split('|');
    if (lowerWords.some((word) => keywordList.some((keyword) => word.includes(keyword)))) {
      hints.push(category);
    }
  }

  // From tags if available
  if (tags.length > 0) {
    const tagMap: { [key: string]: string } = {
      saas: 'SaaS & Software',
      fintech: 'Financial Technology',
      healthcare: 'Healthcare',
      education: 'EdTech',
    };

    tags.forEach((tag) => {
      const lowerTag = tag.toLowerCase();
      if (tagMap[lowerTag]) hints.push(tagMap[lowerTag]);
    });
  }

  return [...new Set(hints)].slice(0, 3);
}

function generateQuickReasons(analysis: DomainAnalysis): string[] {
  const reasons: string[] = [];

  // Length-based reasons
  if (analysis.length <= 8) {
    reasons.push('Short & brandable');
  } else if (analysis.length <= 12) {
    reasons.push('Memorable length');
  }

  // TLD reasons
  if (analysis.tldTier === 'premium') {
    reasons.push('High-trust TLD');
  } else if (analysis.tldTier === 'mainstream') {
    reasons.push('Startup-friendly TLD');
  } else {
    reasons.push('Distinctive TLD');
  }

  // Clarity reasons
  if (analysis.clarity === 'high') {
    reasons.push('Clear to say & spell');
  }

  // Keyword reasons
  if (analysis.keywordStrength === 'high') {
    reasons.push('Exact-match service keyword');
  }

  // Brandability reasons
  if (analysis.brandability === 'high') {
    if (analysis.words <= 2) {
      reasons.push('Clean two-word brand');
    } else {
      reasons.push('Strong brand potential');
    }
  }

  // Versatility
  if (analysis.versatility === 'high') {
    reasons.push('Versatile category name');
  }

  // Niche fit
  if (analysis.nicheHints.length > 0) {
    reasons.push(`Perfect for ${analysis.nicheHints[0]}`);
  }

  // Bundle potential
  if (analysis.words === 2 && !analysis.hasHyphen) {
    reasons.push('Great for bundles & expansion');
  }

  return reasons.slice(0, 7); // Max 7 reasons
}

export function analyzeDomain(
  name: string,
  opts: { bin?: number; tld?: string; tags?: string[] } = {},
): DomainAnalysis {
  const fullDomain = name.includes('.') ? name : `${name}.com`;
  const [domainName, ...tldParts] = fullDomain.split('.');
  const tld = opts.tld || `.${tldParts.join('.')}`;

  const words = extractWords(domainName);
  const length = domainName.length;
  const hasHyphen = domainName.includes('-');
  const hasNumber = /\d/.test(domainName);
  const tldTier = getTldTier(tld);
  const tldLabel = getTldLabel(tldTier);

  // Calculate all factor scores (0-100 each)
  const lengthScore = calculateLengthScore(length);
  const pronounceScore = calculatePronounceabilityScore(words, hasHyphen);
  const clarityScore = calculateClarityScore(length, hasHyphen, hasNumber);
  const brandabilityScore = calculateBrandabilityScore(length, words);
  const keywordScore = calculateKeywordScore(words);
  const tldScore = calculateTldScore(tldTier);
  const memorabilityScore = calculateMemorabilityScore(length, words);
  const versatilityScore = calculateVersatilityScore(words);
  const liquidityScore = calculateLiquidityScore(tldTier, length, !!opts.bin);
  const bundleScore = calculateBundlePotentialScore(words);

  // Calculate weighted composite score
  const rawScore = Math.round(
    lengthScore * 0.15 +
      pronounceScore * 0.12 +
      clarityScore * 0.12 +
      brandabilityScore * 0.14 +
      keywordScore * 0.12 +
      tldScore * 0.12 +
      memorabilityScore * 0.1 +
      versatilityScore * 0.06 +
      liquidityScore * 0.05 +
      bundleScore * 0.02,
  );

  // Friendly remap to 50-100 range
  const finalScore = 50 + Math.round(rawScore * 0.5);

  // Convert scores to categorical ratings
  const brandability: 'high' | 'med' | 'low' =
    brandabilityScore >= 75 ? 'high' : brandabilityScore >= 60 ? 'med' : 'low';
  const clarity: 'high' | 'med' | 'low' =
    clarityScore >= 80 ? 'high' : clarityScore >= 65 ? 'med' : 'low';
  const keywordStrength: 'high' | 'med' | 'low' =
    keywordScore >= 70 ? 'high' : keywordScore >= 50 ? 'med' : 'low';
  const memorability: 'high' | 'med' | 'low' =
    memorabilityScore >= 75 ? 'high' : memorabilityScore >= 60 ? 'med' : 'low';
  const versatility: 'high' | 'med' | 'low' =
    versatilityScore >= 75 ? 'high' : versatilityScore >= 65 ? 'med' : 'low';
  const liquidity: 'high' | 'med' | 'low' =
    liquidityScore >= 75 ? 'high' : liquidityScore >= 60 ? 'med' : 'low';

  const nicheHints = generateNicheHints(words, tld, opts.tags);

  const analysis: DomainAnalysis = {
    length,
    words: words.length,
    hasHyphen,
    hasNumber,
    tld,
    tldTier,
    tldLabel,
    brandability,
    clarity,
    keywordStrength,
    memorability,
    versatility,
    liquidity,
    nicheHints,
    quickReasons: [],
    score: finalScore,
  };

  analysis.quickReasons = generateQuickReasons(analysis);

  return analysis;
}

export function whyThisName({
  name,
  analysis,
}: {
  name: string;
  analysis: DomainAnalysis;
}): string {
  const domainName = name.split('.')[0];
  const topReasons = analysis.quickReasons.slice(0, 3);

  let explanation = `${domainName} is a strong domain choice`;

  // Reference top strengths
  if (topReasons.length >= 2) {
    explanation += ` because it's ${topReasons[0].toLowerCase()} and offers ${topReasons[1].toLowerCase()}`;
  } else if (topReasons.length === 1) {
    explanation += ` because it's ${topReasons[0].toLowerCase()}`;
  }

  explanation += '.';

  // Add TLD context with positive framing
  if (analysis.tldTier === 'premium') {
    explanation += ` The ${analysis.tld} extension provides maximum credibility and is instantly recognizable to customers worldwide.`;
  } else if (analysis.tldTier === 'mainstream') {
    explanation += ` The ${analysis.tld} extension is modern and tech-forward, making it ideal for digital-first businesses.`;
  } else {
    explanation += ` The ${analysis.tld} extension creates a distinctive identity that helps you stand out in your market.`;
  }

  // Add niche context if applicable
  if (analysis.nicheHints.length > 0 && analysis.score >= 75) {
    explanation += ` This name is particularly well-positioned for ${analysis.nicheHints[0].toLowerCase()} companies looking to establish market leadership.`;
  }

  return explanation;
}
